// v5.45.0 split payments (admin only): the part-rows editor (Mark paid → "Split payment", and Edit split) and the
// parts panel on a paid batch (status per part, "Mark this part paid", Edit split). Logic helpers: payoutSplit.js.
import React, { useState } from 'react';
import { adminBonuses, payoutBatches, PAYING_COMPANY_OPTIONS } from './api';
import { blankPart, carryStatus, carryText, fmtCents, fmtSignedCents, paidOwedLine, partLine, partsPayload, rowsFromParts, splitState, toCents } from './payoutSplit';

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join('; ');
  return d || e?.message || 'Unknown error';
};
const todayCT = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });

export function SplitEditor({ rows, setRows, total, t, allowDiff = false }) {
  const st = splitState(rows, total, { allowDiff });
  const set = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const inp = { padding: '5px 7px', fontSize: 12, border: `1px solid ${t.borderColor}`, borderRadius: 4, background: t.bg, color: t.textPrimary, boxSizing: 'border-box' };
  return (
    <div data-testid="split-editor">
      {rows.map((r, i) => (
        <div key={r.key} data-testid={`split-row-${i}`}
          style={{ display: 'grid', gridTemplateColumns: '28px 1.4fr 130px 90px 150px 70px', gap: 8, alignItems: 'center', marginBottom: 6, opacity: r.locked ? 0.75 : 1 }}>
          <span style={{ fontSize: 11, color: t.textSecondary }}>#{i + 1}</span>
          <select value={r.paying_company} disabled={r.locked} data-testid={`split-company-${i}`}
            onChange={(e) => set(i, { paying_company: e.target.value })}
            style={{ ...inp, borderColor: r.paying_company ? t.borderColor : '#f5c97a' }}>
            <option value="" disabled>— Company —</option>
            {PAYING_COMPANY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <input type="text" inputMode="decimal" value={r.amount} disabled={r.locked} placeholder="0.00" data-testid={`split-amount-${i}`}
            onChange={(e) => set(i, { amount: e.target.value })} style={{ ...inp, textAlign: 'right' }} />
          <label style={{ fontSize: 12, color: t.textPrimary, display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={!!r.delayed} disabled={r.locked} data-testid={`split-delayed-${i}`}
              onChange={(e) => set(i, { delayed: e.target.checked, expected_date: e.target.checked ? r.expected_date : '' })} />
            Delayed
          </label>
          {r.delayed
            ? <input type="date" value={r.expected_date} disabled={r.locked} title="Expected date (optional)" data-testid={`split-expected-${i}`}
                onChange={(e) => set(i, { expected_date: e.target.value })} style={inp} />
            : <span />}
          {r.locked
            ? <span style={{ fontSize: 10, color: '#a8d8a8' }} title="Posted in Books — void its check in Books to change it">In Books</span>
            : rows.length > 1
              ? <button type="button" data-testid={`split-remove-${i}`} onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                  style={{ background: 'none', border: 'none', color: '#e0a8a8', fontSize: 11, cursor: 'pointer', padding: 0 }}>Remove</button>
              : <span />}
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 4 }}>
        <button type="button" data-testid="split-add" disabled={rows.length >= 20}
          onClick={() => setRows((rs) => [...rs, blankPart(st.diffC > 0 ? (st.diffC / 100).toFixed(2) : '')])}
          style={{ background: 'none', border: 'none', color: '#5BA3D9', fontSize: 12, cursor: 'pointer', padding: 0 }}>+ Add part</button>
        <span data-testid="split-total" style={{ fontSize: 12, fontWeight: 600, color: st.diffC === 0 ? '#a8d8a8' : '#f5c97a' }}>
          Parts {fmtCents(st.sumC)} of {fmtCents(st.totalC)}{allowDiff ? ' owed' : ''}
          {st.diffC !== 0 && (allowDiff
            ? ` · ${fmtCents(Math.abs(st.diffC))} ${st.diffC > 0 ? 'less' : 'more'} than owed`
            : ` · ${st.diffC > 0 ? 'still to assign' : 'over by'} ${fmtCents(Math.abs(st.diffC))}`)}
        </span>
      </div>
      {st.problems.length > 0 && (
        <div data-testid="split-problems" style={{ fontSize: 11, color: '#f5c97a', marginTop: 4 }}>{st.problems.join(' · ')}</div>
      )}
    </div>
  );
}

// v5.49.0 a regular batch paid more / less than it owes: the difference line + the required reason.
// keepReason = the reason of the batch's waiting carry-over (Edit split keeps it when the box is left empty).
export function CarryNotice({ diffC, employee, reason, setReason, t, keepReason = '' }) {
  if (!diffC) return null;
  return (
    <div data-testid="carry-notice" style={{ marginTop: 8, padding: '8px 10px', border: '1px solid #f5c97a', borderRadius: 5 }}>
      <div data-testid="carry-text" style={{ fontSize: 12, fontWeight: 600, color: '#f5c97a', marginBottom: 6 }}>{carryText(diffC, employee)}</div>
      <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 3 }}>
        Reason <span style={{ color: '#e0a8a8' }}>*</span>{keepReason ? ' (leave empty to keep the current one)' : ''}
      </div>
      <input type="text" data-testid="carry-reason" value={reason} maxLength={500}
        placeholder={keepReason || 'Why the payment differs (shown to the employee)'}
        onChange={(e) => setReason(e.target.value)}
        style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `1px solid ${reason.trim() || keepReason ? t.borderColor : '#f5c97a'}`, borderRadius: 5, background: t.bg, color: t.textPrimary, boxSizing: 'border-box' }} />
    </div>
  );
}

// v5.49.0 "Paid $X (owed $Y; -$D carried to next period)" + reason + where it went — on any paid batch with a carry-over.
export function CarryInfo({ batch, t }) {
  const c = batch.carryover;
  if (!c || batch.status !== 'paid') return null;
  const cancelled = c.status === 'cancelled';
  return (
    <div data-testid="batch-carryover" style={{ padding: '8px 12px', marginBottom: 14, background: t.panelBg, border: `1px solid ${cancelled ? t.borderColor : '#f5c97a'}`, borderRadius: 6, fontSize: 12, color: t.textPrimary }}>
      {!cancelled && <div data-testid="batch-carryover-line" style={{ fontWeight: 600 }}>{paidOwedLine(c)}</div>}
      <div style={{ color: t.textSecondary, marginTop: 2 }}>
        Carry-over ({c.direction}){cancelled ? '' : ` — ${c.reason}`} · <span data-testid="batch-carryover-status">{carryStatus(c)}</span>
      </div>
    </div>
  );
}

// The parts of a PAID batch, under the totals strip.
export function PartsPanel({ batch, isAdmin, t, onChanged }) {
  const parts = batch.parts || [];
  const [payingPart, setPayingPart] = useState(null);     // part id whose "Mark this part paid" is open
  const [paidDate, setPaidDate] = useState(todayCT());
  const [editRows, setEditRows] = useState(null);          // null = not editing
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [reason, setReason] = useState('');
  if (batch.status !== 'paid' || parts.length === 0) return null;
  // v5.49.0 a regular batch may be paid more / less than owed (carry-over) — unless its carry-over was already applied
  // (then the paid total is frozen); a renewal-bonus payout stays exact.
  const carry = batch.carryover;
  const frozen = !!(carry && carry.locked);
  const allowDiff = batch.kind !== 'renewal_bonus' && !frozen;
  const editTotal = frozen ? carry.paid_amount : batch.total_amount;
  const st = editRows ? splitState(editRows, editTotal, { allowDiff }) : null;
  const keepReason = carry && carry.status === 'waiting' ? carry.reason : '';
  const diffC = st && allowDiff ? st.diffC : 0;
  const canSave = !!st?.ready && (!diffC || !!reason.trim() || !!keepReason);
  const btn = { padding: '5px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' };

  const markPaid = async (p) => {
    setBusy(true); setErr('');
    try {
      await payoutBatches.markPartPaid(batch.id, p.id, paidDate);
      setPayingPart(null); setMsg(`Part ${p.seq} marked paid.`);
      await onChanged();
    } catch (e) { setErr('Mark part paid failed: ' + errText(e)); }
    setBusy(false);
  };
  const openEdit = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await payoutBatches.parts(batch.id);
      setEditRows(rowsFromParts(r.data.parts)); setReason('');
    } catch (e) { setErr('Edit split: ' + errText(e)); }
    setBusy(false);
  };
  const saveEdit = async () => {
    if (!canSave) return;
    if (!window.confirm(`Save the new split of batch #${batch.id}?${diffC ? `\n${carryText(diffC, batch.closed_by_name)}` : ''}`)) return;
    setBusy(true); setErr('');
    try {
      await payoutBatches.editSplit(batch.id, partsPayload(editRows, true), reason.trim() || null);
      setEditRows(null); setMsg('Split saved.');
      await onChanged();
    } catch (e) { setErr('Save split failed: ' + errText(e)); }
    setBusy(false);
  };

  return (
    <div data-testid="parts-panel" style={{ padding: 12, marginBottom: 14, background: t.panelBg, border: `1px solid ${t.borderColor}`, borderRadius: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary }}>
          {parts.length > 1 ? `Paid in ${parts.length} parts` : 'Payment'}
        </span>
        {isAdmin && !editRows && (
          <button type="button" data-testid="split-edit" disabled={busy} onClick={openEdit}
            style={{ ...btn, marginLeft: 'auto', background: t.panelBg, color: '#5BA3D9', border: `1px solid ${t.borderColor}` }}>Edit split</button>
        )}
      </div>
      {!editRows && parts.map((p) => (
        <div key={p.id} data-testid={`part-${p.seq}`} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: t.textPrimary, padding: '3px 0' }}>
          <span style={{ color: t.textSecondary, width: 70 }}>Part {p.seq} of {p.of}</span>
          <span data-testid={`part-line-${p.seq}`}>{partLine(p)}</span>
          <span style={{ padding: '1px 7px', borderRadius: 3, fontSize: 10, fontWeight: 700,
            background: p.status === 'delayed' ? '#3a2a14' : '#1a3a1a', color: p.status === 'delayed' ? '#f5c97a' : '#a8d8a8' }}>
            {p.status === 'delayed' ? 'PENDING' : 'PAID'}
          </span>
          {isAdmin && p.status === 'delayed' && payingPart !== p.id && (
            <button type="button" data-testid={`part-pay-${p.seq}`} onClick={() => { setPaidDate(todayCT()); setPayingPart(p.id); setErr(''); }}
              style={{ background: 'none', border: 'none', color: '#5BA3D9', fontSize: 11, cursor: 'pointer', padding: 0 }}>Mark this part paid</button>
          )}
          {payingPart === p.id && (
            <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: t.textSecondary }}>Paid on</span>
              <input type="date" value={paidDate} max={todayCT()} data-testid={`part-pay-date-${p.seq}`} onChange={(e) => setPaidDate(e.target.value)}
                style={{ padding: '3px 6px', fontSize: 11, border: `1px solid ${t.borderColor}`, borderRadius: 4, background: t.bg, color: t.textPrimary }} />
              <button type="button" data-testid={`part-pay-confirm-${p.seq}`} disabled={busy || !paidDate} onClick={() => markPaid(p)}
                style={{ ...btn, border: 'none', background: '#185FA5', color: '#fff' }}>{busy ? 'Saving…' : 'Confirm'}</button>
              <button type="button" onClick={() => setPayingPart(null)}
                style={{ ...btn, background: t.panelBg, color: t.textSecondary, border: `1px solid ${t.borderColor}` }}>Cancel</button>
            </span>
          )}
        </div>
      ))}
      {editRows && (
        <div>
          <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 6 }}>
            Parts already posted in Books can't change here (void the check in Books first).{' '}
            {allowDiff ? 'A total different from what is owed carries to the next payout (reason required).'
              : frozen ? `This batch's carry-over was already applied — the parts must add up exactly to ${fmtCents(toCents(carry.paid_amount))} (fix it on the batch it was applied to).`
              : 'The parts must add up exactly to the batch total.'}
          </div>
          <SplitEditor rows={editRows} setRows={setEditRows} total={editTotal} t={t} allowDiff={allowDiff} />
          <CarryNotice diffC={diffC} employee={batch.closed_by_name} reason={reason} setReason={setReason} t={t} keepReason={keepReason} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={() => setEditRows(null)} disabled={busy}
              style={{ ...btn, background: t.panelBg, color: t.textSecondary, border: `1px solid ${t.borderColor}` }}>Cancel</button>
            <button type="button" data-testid="split-save" disabled={busy || !canSave} onClick={saveEdit}
              style={{ ...btn, border: 'none', background: canSave ? '#185FA5' : '#444', color: '#fff', cursor: canSave && !busy ? 'pointer' : 'not-allowed' }}>
              {busy ? 'Saving…' : 'Save split'}
            </button>
          </div>
        </div>
      )}
      {msg && <div data-testid="parts-msg" style={{ fontSize: 12, color: '#a8d8a8', marginTop: 6 }}>✓ {msg}</div>}
      {err && <div data-testid="parts-err" style={{ fontSize: 12, color: '#e0a8a8', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

// Admin "Delayed bonus parts" list (Bonus dashboard): employee, batch, company, amount, expected date.
export function DelayedParts({ reloadKey, panelBg, borderColor, textPrimary, textSecondary }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  React.useEffect(() => {
    let live = true;
    payoutBatches.delayedParts()
      .then((r) => { if (live) { setData(r.data); setErr(''); } })
      .catch((e) => { if (live) setErr(errText(e)); });
    return () => { live = false; };
  }, [reloadKey]);
  if (err) return <div style={{ fontSize: 12, color: '#e0a8a8' }}>{err}</div>;
  if (!data) return <div style={{ fontSize: 12, color: textSecondary }}>Loading…</div>;
  if (data.rows.length === 0) return <div data-testid="delayed-none" style={{ fontSize: 12, color: textSecondary }}>No delayed parts.</div>;
  const fmtD = (d) => { const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[2]}/${m[3]}/${m[1]}` : '—'; };
  const cols = '1.3fr 120px 1.2fr 110px 110px';
  return (
    <div data-testid="delayed-parts" style={{ border: `1px solid ${borderColor}`, borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 6, padding: '6px 10px', fontSize: 10, color: textSecondary, fontWeight: 600, textTransform: 'uppercase', background: panelBg }}>
        <span>Employee</span><span>Batch</span><span>Company</span><span style={{ textAlign: 'right' }}>Amount</span><span>Expected</span>
      </div>
      {data.rows.map((r) => (
        <div key={r.part_id} data-testid={`delayed-row-${r.part_id}`} style={{ display: 'grid', gridTemplateColumns: cols, gap: 6, padding: '6px 10px', fontSize: 12, color: textPrimary, borderTop: `1px solid ${borderColor}` }}>
          <span>{r.producer_name || '—'}</span>
          <span>#{r.batch_id} · part {r.seq} of {r.of}</span>
          <span>{r.paying_company_name}</span>
          <span style={{ textAlign: 'right', fontWeight: 600 }}>{fmtCents(Math.round(Number(r.amount) * 100))}</span>
          <span>{fmtD(r.expected_date)}</span>
        </div>
      ))}
      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 6, padding: '6px 10px', fontSize: 12, fontWeight: 600, color: textPrimary, borderTop: `1px solid ${borderColor}` }}>
        <span>Total</span><span /><span /><span style={{ textAlign: 'right' }}>{fmtCents(Math.round(Number(data.total) * 100))}</span><span />
      </div>
    </div>
  );
}

// v5.49.0 admin "Bonus carry-overs" list (Bonus dashboard): every carry-over still waiting for an employee's next payout.
export function CarryoverList({ reloadKey, panelBg, borderColor, textPrimary, textSecondary }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  React.useEffect(() => {
    let live = true;
    adminBonuses.carryovers()
      .then((r) => { if (live) { setData(r.data); setErr(''); } })
      .catch((e) => { if (live) setErr(errText(e)); });
    return () => { live = false; };
  }, [reloadKey]);
  if (err) return <div style={{ fontSize: 12, color: '#e0a8a8' }}>{err}</div>;
  if (!data) return <div style={{ fontSize: 12, color: textSecondary }}>Loading…</div>;
  if (data.rows.length === 0) return <div data-testid="carryovers-none" style={{ fontSize: 12, color: textSecondary }}>No carry-overs waiting.</div>;
  const cols = '1.2fr 90px 110px 1.8fr 110px 1.2fr';
  return (
    <div data-testid="carryover-list" style={{ border: `1px solid ${borderColor}`, borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 6, padding: '6px 10px', fontSize: 10, color: textSecondary, fontWeight: 600, textTransform: 'uppercase', background: panelBg }}>
        <span>Employee</span><span>From batch</span><span>Paid</span><span>Reason</span><span style={{ textAlign: 'right' }}>Waiting</span><span>Status</span>
      </div>
      {data.rows.map((r) => (
        <div key={r.id} data-testid={`carryover-row-${r.id}`} style={{ display: 'grid', gridTemplateColumns: cols, gap: 6, padding: '6px 10px', fontSize: 12, color: textPrimary, borderTop: `1px solid ${borderColor}` }}>
          <span>{r.staff_name || '—'}</span>
          <span>#{r.source_batch_id}</span>
          <span>{r.source_paid_date || '—'}</span>
          <span title={paidOwedLine(r)}>{r.direction} — {r.reason}</span>
          <span style={{ textAlign: 'right', fontWeight: 600, color: Number(r.remaining) < 0 ? '#e0a8a8' : '#a8d8a8' }}>{fmtSignedCents(toCents(r.remaining))}</span>
          <span style={{ color: textSecondary }}>{carryStatus(r)}</span>
        </div>
      ))}
      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 6, padding: '6px 10px', fontSize: 12, fontWeight: 600, color: textPrimary, borderTop: `1px solid ${borderColor}` }}>
        <span>Total</span><span /><span /><span /><span style={{ textAlign: 'right' }}>{fmtSignedCents(toCents(data.total))}</span><span />
      </div>
    </div>
  );
}
